import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, TextField, MenuItem,
  Alert, CircularProgress, Grid,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

const SPECIALTIES = [
  'Civil Engineering', 'Electrical Works', 'Plumbing & Water Supply',
  'Irrigation Systems', 'Land Clearing & Preparation', 'Construction & Building',
  'Pest Control', 'Agronomy Consulting', 'Veterinary Services',
  'Transportation & Logistics', 'Equipment Maintenance', 'IT & Technology',
  'Security Services', 'Catering & Food Services', 'Other',
];
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { contractorService } from '@smartgrow/shared';
import type { LaborType } from '@smartgrow/shared/types';
import { AttachmentsPanel } from '../../components/AttachmentsPanel';

export const EditContractorPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: contractor, isLoading } = useQuery({
    queryKey: ['contractors', id],
    queryFn: () => contractorService.getContractorById(id!),
    enabled: !!id,
  });

  const [form, setForm] = useState({
    contractorCode: '',
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    specialty: '',
    laborType: 'FORMAL' as LaborType,
    isActive: true,
    notes: '',
  });

  useEffect(() => {
    if (contractor) {
      setForm({
        contractorCode: contractor.contractorCode,
        companyName: contractor.companyName,
        contactPerson: contractor.contactPerson ?? '',
        email: contractor.email ?? '',
        phone: contractor.phone ?? '',
        address: contractor.address ?? '',
        specialty: contractor.specialty ?? '',
        laborType: contractor.laborType ?? 'FORMAL',
        isActive: contractor.isActive,
        notes: contractor.notes ?? '',
      });
    }
  }, [contractor]);

  const mutation = useMutation({
    mutationFn: (data: typeof form) =>
      contractorService.updateContractor(id!, {
        ...data,
        contactPerson: data.contactPerson || undefined,
        email: data.email || undefined,
        phone: data.phone || undefined,
        address: data.address || undefined,
        specialty: data.specialty || undefined,
        notes: data.notes || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contractors'] });
      navigate('/dashboard/contractors');
    },
  });

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.companyName.trim()) e.companyName = 'Company name is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const set = (field: string, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined as any }));
  };

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  if (!contractor) {
    return <Alert severity="error">Contractor not found.</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined" size="large">Back</Button>
        <Box>
          <Typography variant="h4" fontWeight="bold">Edit Contractor</Typography>
          <Typography variant="body2" color="text.secondary">{contractor.contractorCode} — {contractor.companyName}</Typography>
        </Box>
      </Box>

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>Failed to update contractor. Please try again.</Alert>
      )}

      <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>Contractor Details</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Contractor Code"
              value={form.contractorCode}
              fullWidth disabled
              helperText="Contractor code cannot be changed"
              size="medium"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Company Name *"
              value={form.companyName}
              onChange={(e) => set('companyName', e.target.value)}
              fullWidth error={!!errors.companyName} helperText={errors.companyName} size="medium"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select label="Specialty" value={form.specialty} onChange={(e) => set('specialty', e.target.value)} fullWidth size="medium">
              <MenuItem value="">— Select specialty —</MenuItem>
              {SPECIALTIES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Contact Person" value={form.contactPerson} onChange={(e) => set('contactPerson', e.target.value)} fullWidth size="medium" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} fullWidth size="medium" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select label="Labor Type" value={form.laborType}
              onChange={(e) => set('laborType', e.target.value as LaborType)} fullWidth size="medium"
              helperText="Casual/day laborers are tracked separately in labor-cost reports">
              <MenuItem value="FORMAL">Formal Contractor</MenuItem>
              <MenuItem value="CASUAL_DAY_LABOR">Casual / Day Labor</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} fullWidth size="medium" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Address" value={form.address} onChange={(e) => set('address', e.target.value)} fullWidth size="medium" />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Notes (optional)" multiline rows={3}
              value={form.notes} onChange={(e) => set('notes', e.target.value)}
              fullWidth inputProps={{ maxLength: 500 }} size="medium"
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <AttachmentsPanel entityType="CONTRACTOR" entityId={id!} />
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pb: 4 }}>
        <Button size="large" onClick={() => navigate('/dashboard/contractors')} disabled={mutation.isPending}>Cancel</Button>
        <Button
          size="large" variant="contained"
          onClick={() => validate() && mutation.mutate(form)}
          disabled={mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={20} /> : null}
          sx={{ minWidth: 160 }}
        >
          {mutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
};
